// ============================================================================
// AdminFarmsPanel — farm identity + members + FSA chain (Build S1, Task #21).
// ============================================================================
// Sidebar entry: #admin/farms. UIF only.
//
// Two modes (route-driven, matching the events-edit/budget param precedent —
// no accordion rows, per house convention):
//   * LIST   (#admin/farms):     KPI row, search + AMP filter, farms table,
//                                Add-farm modal. Row click opens the detail.
//   * DETAIL (#admin/farms/:id): farm header + edit/archive, members CRUD,
//                                email-chain card (the Module J routing fix,
//                                read-only surface of getEmailRecipientsForFarm),
//                                FSA identity chain (read-only in S1),
//                                subfields table with unresolved-link flags.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Plus, AlertCircle, Mail, Edit2, Archive, ArrowLeft,
  Tractor, Users, MapPin, Star, X as XIcon, CheckCircle2, PenLine,
} from 'lucide-react';
import {
  api,
  type AdminFarm,
  type AdminFarmDetail,
  type AdminFarmer,
  type FarmKpis,
  type FarmMember,
  type FarmCreateInput,
  type FarmMemberCreateInput,
  type RelationshipToFarm,
} from '../api';

interface Props {
  token: string;
  /** When set, the panel renders the detail view for that farm. */
  farmId?: number;
}

const RELATIONSHIPS: RelationshipToFarm[] = [
  'owner', 'operator', 'manager', 'spouse', 'family', 'agent',
];

export function AdminFarmsPanel({ token, farmId }: Props) {
  if (farmId !== undefined) return <FarmDetail token={token} farmId={farmId} />;
  return <FarmList token={token} />;
}

// ============================================================================
// LIST MODE
// ============================================================================
function FarmList({ token }: { token: string }) {
  const [farms, setFarms] = useState<AdminFarm[]>([]);
  const [kpis, setKpis] = useState<FarmKpis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [ampFilter, setAmpFilter] = useState<string>('all');
  const [includeTest, setIncludeTest] = useState(false);   // §5.4: show test farms
  const [addOpen, setAddOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.adminFarms(token, {
        q: search.trim() || undefined,
        amp: ampFilter === 'all' ? undefined : (ampFilter as 'true' | 'false'),
        include_test: includeTest || undefined,
      });
      setFarms(res.farms);
      setKpis(res.kpis);
    } catch (e: any) {
      setError(e?.message || 'Could not load farms');
    } finally {
      setLoading(false);
    }
  }, [token, search, ampFilter, includeTest]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-uif-green-dark flex items-center gap-2">
            <Tractor className="h-5 w-5" /> Farms
          </h1>
          <p className="text-sm text-gray-500">
            Farm identity, members and the FSA chain — the AMP program foundation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            Add farm
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Farms" value={kpis?.total_farms ?? 0} tone="ok" icon={<Tractor className="h-4 w-4" />} />
        <Kpi label="AMP participants" value={kpis?.amp_participants ?? 0} tone="amp" icon={<Users className="h-4 w-4" />} />
        <Kpi label="Unresolved FSA IDs" value={kpis?.farms_with_unresolved_fsa ?? 0} tone="warn" icon={<AlertCircle className="h-4 w-4" />} />
        <Kpi label="On email chain" value={kpis?.members_on_email_chain ?? 0} tone="ok" icon={<Mail className="h-4 w-4" />} />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[200px]">
          <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code, name or county"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">AMP</span>
          <select
            value={ampFilter}
            onChange={(e) => setAmpFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="all">All</option>
            <option value="true">AMP participants</option>
            <option value="false">Non-AMP</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 self-end pb-1.5">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
          Show test farms
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {/* Farms table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Farm</th>
              <th className="px-3 py-2">County</th>
              <th className="px-3 py-2">FSA farm #</th>
              <th className="px-3 py-2 text-right">Members</th>
              <th className="px-3 py-2 text-right">Subfields</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && farms.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!loading && farms.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No farms found.</td></tr>
            )}
            {farms.map((f) => (
              <tr
                key={f.farm_id}
                role="link"
                tabIndex={0}
                aria-label={`Open farm ${f.farm_code} — ${f.farm_name}`}
                className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-uif-green focus:ring-inset"
                onClick={() => { window.location.hash = `#admin/farms/${f.farm_id}`; }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.location.hash = `#admin/farms/${f.farm_id}`;
                  }
                }}
              >
                <td className="px-3 py-2 font-mono font-semibold text-uif-green-dark">{f.farm_code}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{f.farm_name}</div>
                  {f.legal_entity_name && <div className="text-xs text-gray-500">{f.legal_entity_name}</div>}
                </td>
                <td className="px-3 py-2 text-gray-700">{f.county ?? '—'}</td>
                <td className="px-3 py-2 text-gray-700">{f.usda_farm_number ?? '—'}</td>
                <td className="px-3 py-2 text-right text-gray-700">{f.member_count}</td>
                <td className="px-3 py-2 text-right text-gray-700">
                  {f.subfield_count}
                  {f.unresolved_subfield_count > 0 && (
                    <span
                      className="ml-1.5"
                      title={`${f.unresolved_subfield_count} subfield(s) without a resolved FSA field link`}
                    >
                      <Pill tone="warn">{f.unresolved_subfield_count} unresolved</Pill>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {f.amp_participant && <Pill tone="amp">AMP</Pill>}
                    {f.w9_on_file && <Pill tone="ok">W-9</Pill>}
                    {f.is_test && <Pill tone="warn">TEST</Pill>}
                    {f.is_archived && <Pill tone="warn">Archived</Pill>}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <a
                    href={`#admin/farms/${f.farm_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-uif-green-dark hover:underline"
                  >
                    Open →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <FarmModal
          token={token}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); void refresh(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// DETAIL MODE
// ============================================================================
function FarmDetail({ token, farmId }: { token: string; farmId: number }) {
  const [detail, setDetail] = useState<AdminFarmDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [memberModal, setMemberModal] = useState<{ mode: 'add' } | { mode: 'edit'; member: FarmMember } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setDetail(await api.adminFarm(token, farmId));
    } catch (e: any) {
      setError(e?.message || 'Could not load farm');
    } finally {
      setLoading(false);
    }
  }, [token, farmId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function archiveFarm() {
    if (!detail) return;
    if (!window.confirm(`Archive ${detail.farm.farm_code} — ${detail.farm.farm_name}? The farm is hidden from the list but nothing is deleted.`)) return;
    try {
      await api.archiveFarm(token, farmId);
      window.location.hash = '#admin/farms';
    } catch (e: any) {
      setError(e?.message || 'Could not archive farm');
    }
  }

  async function archiveMember(m: FarmMember) {
    if (!window.confirm(`Remove ${m.member_name} from this farm? (archived, not deleted)`)) return;
    try {
      await api.archiveFarmMember(token, farmId, m.member_id);
      void refresh();
    } catch (e: any) {
      setError(e?.message || 'Could not archive member');
    }
  }

  if (loading && !detail) {
    return (
      <div className="py-16 text-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin inline" /> Loading farm…
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="space-y-3">
        <BackLink />
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
          {error || 'Farm not found.'}
        </div>
      </div>
    );
  }

  const { farm, members, fsa, subfields, email_recipients } = detail;
  const supersededValues = farm.json_config?.['superseded_values'];

  return (
    <div className="space-y-4">
      <BackLink />

      {/* Farm header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-bold text-lg text-uif-green-dark">{farm.farm_code}</span>
              <h1 className="text-xl font-bold text-gray-900">{farm.farm_name}</h1>
              {farm.amp_participant && <Pill tone="amp">AMP</Pill>}
              {farm.w9_on_file && <Pill tone="ok">W-9 on file</Pill>}
              {farm.is_archived && <Pill tone="warn">Archived</Pill>}
            </div>
            {farm.legal_entity_name && <div className="text-sm text-gray-500 mt-0.5">{farm.legal_entity_name}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Edit2 className="h-4 w-4" /> Edit
            </button>
            {!farm.is_archived && (
              <button
                type="button"
                onClick={() => void archiveFarm()}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50"
              >
                <Archive className="h-4 w-4" /> Archive
              </button>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <InfoItem label="County" value={farm.county} />
          <InfoItem label="State" value={farm.state_code} />
          <InfoItem label="USDA farm #" value={farm.usda_farm_number} />
          <InfoItem label="Service center" value={farm.service_center} />
          <InfoItem label="Conservation district" value={farm.conservation_district} />
          <InfoItem
            label="Address"
            value={[farm.address_line_1, farm.address_line_2, farm.address_city, farm.address_state, farm.address_zip].filter(Boolean).join(', ') || null}
          />
        </dl>

        {/* Pin 3 surface: identity corrections recorded at backfill are visible, not buried. */}
        {supersededValues != null && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
            <span className="font-semibold uppercase tracking-wider">Identity correction on record:</span>{' '}
            superseded tracker FSA values were replaced by the EE Documentation batch —{' '}
            <code className="break-all">{JSON.stringify(supersededValues)}</code>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      )}

      {/* Members */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <Users className="h-4 w-4" /> Members
          </h2>
          <button
            type="button"
            onClick={() => setMemberModal({ mode: 'add' })}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark"
          >
            <Plus className="h-3.5 w-3.5" /> Add member
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Relationship</th>
                <th className="px-3 py-2">Flags</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No members yet.</td></tr>
              )}
              {members.map((m) => (
                <tr key={m.member_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900 inline-flex items-center gap-1.5">
                      {m.is_primary_contact && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" aria-label="Primary contact" />}
                      {m.member_name}
                    </span>
                    {m.farmer_id != null && (
                      <span className="ml-1.5" title={`Linked to portal account #${m.farmer_id}`}>
                        <Pill tone="ok">Portal</Pill>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{m.email ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{m.phone ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700 capitalize">{m.relationship_to_farm}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {m.on_email_chain && <Pill tone="ok">Email chain</Pill>}
                      {m.signature_authority && <Pill tone="amp">Signature</Pill>}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setMemberModal({ mode: 'edit', member: m })}
                      className="text-gray-500 hover:text-uif-green-dark p-1"
                      title="Edit member"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void archiveMember(m)}
                      className="text-gray-500 hover:text-amber-700 p-1"
                      title="Archive member"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Email chain — live surface of getEmailRecipientsForFarm (Module J fix) */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
          <Mail className="h-4 w-4" /> Email chain
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Members receiving farm-scoped email. Program sends adopt this list in a later Build; the
          existing Producers-panel blast is unchanged.
        </p>
        {email_recipients.length === 0 ? (
          <div className="mt-2 text-sm text-gray-400">Nobody on the chain — add a member with an email address.</div>
        ) : (
          <ul className="mt-2 space-y-1">
            {email_recipients.map((r) => (
              <li key={r.member_id} className="text-sm text-gray-700 flex items-center gap-1.5">
                {r.is_primary_contact
                  ? <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                  : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
                <span className="font-medium">{r.member_name}</span>
                <span className="text-gray-500">&lt;{r.email}&gt;</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* FSA identity chain (read-only in S1) */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
          <MapPin className="h-4 w-4" /> FSA identity
        </h2>
        {fsa.length === 0 ? (
          <div className="mt-2 text-sm text-gray-400">No FSA profile on record.</div>
        ) : fsa.map((p) => (
          <div key={p.farm_fsa_profile_id} className="mt-3">
            <div className="text-sm">
              <span className="text-xs uppercase tracking-wider text-gray-500">Producer of record ({p.program_year})</span>
              <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                <PenLine className="h-3.5 w-3.5 text-gray-400" />
                {p.producer_name}
                {p.json_config?.['identity_flag'] === 'not_in_20260526_batch' && (
                  <span title="Producer name comes from the tracker — absent from the EE Documentation batch; verify against AD-2047 before NRCS submission.">
                    <Pill tone="warn">Not in 20260526 batch</Pill>
                  </span>
                )}
              </div>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm border border-gray-100 rounded">
                <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50/60">
                  <tr>
                    <th className="px-3 py-1.5">FSA farm #</th>
                    <th className="px-3 py-1.5">Tract #</th>
                    <th className="px-3 py-1.5">State / county</th>
                    <th className="px-3 py-1.5">Fields</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {p.tracts.map((t) => (
                    <tr key={t.farm_fsa_tract_id}>
                      <td className="px-3 py-1.5 text-gray-900">
                        {t.fsa_farm_number}
                        {t.json_config?.['secondary_fsa_farm'] != null && (
                          <span className="ml-1.5" title="Secondary FSA farm number for this operation">
                            <Pill tone="warn">2nd farm</Pill>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-gray-900">{t.fsa_tract_number}</td>
                      <td className="px-3 py-1.5 text-gray-700">{t.fsa_state_code}-{t.fsa_county_code}</td>
                      <td className="px-3 py-1.5 text-gray-700">
                        {t.fields.length === 0
                          ? <span className="text-gray-400">none</span>
                          : t.fields.map((fl) => fl.fsa_field_number).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* Subfields (read-only in S1) */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-uif-green-dark">Subfields</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            UIF-internal management grain. NRCS only ever sees the FSA-field roll-up.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Prior code</th>
                <th className="px-3 py-2">Land use</th>
                <th className="px-3 py-2">FSA link</th>
                <th className="px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {subfields.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No subfields.</td></tr>
              )}
              {subfields.map((s) => (
                <tr key={s.subfield_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-gray-900">{s.subfield_code}</td>
                  <td className="px-3 py-2 font-mono text-gray-500">{s.prior_uif_subfield_id ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{s.vegetation_cover ?? '—'}</td>
                  <td className="px-3 py-2">
                    {s.farm_fsa_field_id != null ? (
                      <span className="text-gray-700">T {s.fsa_tract_number} · F {s.fsa_field_number}</span>
                    ) : (
                      <span title={s.link_note ?? 'FSA field link not yet resolved'}>
                        <Pill tone="warn">Unresolved</Pill>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs max-w-[280px] truncate" title={s.notes ?? undefined}>
                    {s.notes ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editOpen && (
        <FarmModal
          token={token}
          existing={farm}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); void refresh(); }}
        />
      )}
      {memberModal && (
        <MemberModal
          token={token}
          farmId={farmId}
          member={memberModal.mode === 'edit' ? memberModal.member : undefined}
          onClose={() => setMemberModal(null)}
          onSaved={() => { setMemberModal(null); void refresh(); }}
        />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <a href="#admin/farms" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
      <ArrowLeft className="h-4 w-4" /> All farms
    </a>
  );
}

function InfoItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="text-gray-900">{value ?? '—'}</dd>
    </div>
  );
}

// ============================================================================
// Farm create/edit modal
// ============================================================================
function FarmModal({
  token, existing, onClose, onSaved,
}: {
  token: string;
  existing?: AdminFarmDetail['farm'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FarmCreateInput>({
    farm_code: existing?.farm_code ?? '',
    farm_name: existing?.farm_name ?? '',
    legal_entity_name: existing?.legal_entity_name ?? '',
    county: existing?.county ?? '',
    state_code: existing?.state_code ?? 'MS',
    address_line_1: existing?.address_line_1 ?? '',
    address_line_2: existing?.address_line_2 ?? '',
    address_city: existing?.address_city ?? '',
    address_state: existing?.address_state ?? '',
    address_zip: existing?.address_zip ?? '',
    usda_farm_number: existing?.usda_farm_number ?? '',
    service_center: existing?.service_center ?? '',
    conservation_district: existing?.conservation_district ?? '',
    amp_participant: existing?.amp_participant ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function set<K extends keyof FarmCreateInput>(key: K, value: FarmCreateInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  const norm = (s: string | null | undefined) => (s && s.trim() !== '' ? s.trim() : null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    const payload: FarmCreateInput = {
      farm_code: form.farm_code.trim().toUpperCase(),
      farm_name: form.farm_name.trim(),
      legal_entity_name: norm(form.legal_entity_name),
      county: norm(form.county),
      state_code: (form.state_code || 'MS').trim().toUpperCase(),
      address_line_1: norm(form.address_line_1),
      address_line_2: norm(form.address_line_2),
      address_city: norm(form.address_city),
      address_state: norm(form.address_state)?.toUpperCase() ?? null,
      address_zip: norm(form.address_zip),
      usda_farm_number: norm(form.usda_farm_number),
      service_center: norm(form.service_center),
      conservation_district: norm(form.conservation_district),
      amp_participant: form.amp_participant,
    };
    try {
      if (existing) {
        await api.updateFarm(token, existing.farm_id, payload);
      } else {
        await api.createFarm(token, payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={existing ? `Edit ${existing.farm_code}` : 'Add farm'} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Farm code *">
            <input required value={form.farm_code} onChange={(e) => set('farm_code', e.target.value)}
              placeholder="e.g. ANNB" maxLength={8} className={inputCls} />
          </Field>
          <Field label="Farm name *">
            <input required value={form.farm_name} onChange={(e) => set('farm_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Legal entity">
            <input value={form.legal_entity_name ?? ''} onChange={(e) => set('legal_entity_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="County">
            <input value={form.county ?? ''} onChange={(e) => set('county', e.target.value)} className={inputCls} />
          </Field>
          <Field label="State">
            <input value={form.state_code ?? 'MS'} onChange={(e) => set('state_code', e.target.value)} maxLength={2} className={inputCls} />
          </Field>
          <Field label="USDA farm #">
            <input value={form.usda_farm_number ?? ''} onChange={(e) => set('usda_farm_number', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Service center">
            <input value={form.service_center ?? ''} onChange={(e) => set('service_center', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Conservation district">
            <input value={form.conservation_district ?? ''} onChange={(e) => set('conservation_district', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address line 1">
            <input value={form.address_line_1 ?? ''} onChange={(e) => set('address_line_1', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address line 2">
            <input value={form.address_line_2 ?? ''} onChange={(e) => set('address_line_2', e.target.value)} className={inputCls} />
          </Field>
          <Field label="City">
            <input value={form.address_city ?? ''} onChange={(e) => set('address_city', e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State">
              <input value={form.address_state ?? ''} onChange={(e) => set('address_state', e.target.value)} maxLength={2} className={inputCls} />
            </Field>
            <Field label="ZIP">
              <input value={form.address_zip ?? ''} onChange={(e) => set('address_zip', e.target.value)} maxLength={10} className={inputCls} />
            </Field>
          </div>
        </div>

        <div className="flex items-center gap-5 pt-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.amp_participant ?? false}
              onChange={(e) => set('amp_participant', e.target.checked)} />
            AMP participant
          </label>
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {existing ? 'Save changes' : 'Create farm'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Member add/edit modal
// ============================================================================
function MemberModal({
  token, farmId, member, onClose, onSaved,
}: {
  token: string;
  farmId: number;
  member?: FarmMember;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FarmMemberCreateInput>({
    member_name: member?.member_name ?? '',
    email: member?.email ?? '',
    phone: member?.phone ?? '',
    relationship_to_farm: member?.relationship_to_farm ?? 'operator',
    is_primary_contact: member?.is_primary_contact ?? false,
    on_email_chain: member?.on_email_chain ?? true,
    signature_authority: member?.signature_authority ?? false,
    title_role: member?.title_role ?? '',
    farmer_id: member?.farmer_id ?? null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // S3 chore-ledger item 1: producer LOOKUP (name/email search showing portal
  // id) instead of a raw farmer_id number — the confusion that started the
  // Shawanda/Will Watkins detour in the S3 smoke.
  const [producers, setProducers] = useState<AdminFarmer[]>([]);
  const [producerQuery, setProducerQuery] = useState('');
  // Copilot PR8 r3: the selection is STATE captured at click time — deriving
  // it from the (query-scoped) results list lost the name/email the moment
  // the query cleared after selection.
  const [selectedProducer, setSelectedProducer] = useState<AdminFarmer | null>(null);
  // Copilot PR8 r8: distinguish "searching" from "searched, none found" so
  // the no-match hint never flashes while the debounce/request is in flight.
  const [producerSearching, setProducerSearching] = useState(false);

  // Copilot PR8: server-side search via the list endpoint's existing `q`
  // param (300ms debounce) instead of preloading every producer — payload
  // and work stay constant as the cohort grows.
  useEffect(() => {
    const q = producerQuery.trim();
    if (q.length < 2) { setProducers([]); setProducerSearching(false); return; }
    setProducerSearching(true);
    const timer = setTimeout(() => {
      api.adminFarmers(token, { q })
        .then((r) => setProducers(r.farmers))
        .catch(() => undefined)
        .finally(() => setProducerSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [token, producerQuery]);

  const producerMatches = producerQuery.trim().length >= 2 ? producers.slice(0, 8) : [];
  // Selection state wins; edit-mode pre-existing links fall back to the
  // member row's own identity (name is already on the form).
  const linkedProducer = form.farmer_id != null && selectedProducer?.farmer_id === form.farmer_id
    ? selectedProducer
    : null;
  const linkedLabel = linkedProducer
    ? `${linkedProducer.full_name} (${linkedProducer.email})`
    : member && member.farmer_id === form.farmer_id && form.farmer_id != null
      ? `${member.member_name}${member.email ? ` (${member.email})` : ''}`
      : 'linked producer';

  function set<K extends keyof FarmMemberCreateInput>(key: K, value: FarmMemberCreateInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  const norm = (s: string | null | undefined) => (s && s.trim() !== '' ? s.trim() : null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    const payload: FarmMemberCreateInput = {
      member_name: form.member_name.trim(),
      email: norm(form.email),
      phone: norm(form.phone),
      relationship_to_farm: form.relationship_to_farm,
      is_primary_contact: form.is_primary_contact,
      on_email_chain: form.on_email_chain,
      signature_authority: form.signature_authority,
      title_role: norm(form.title_role),
      // null clears the link; the producer-side farm view keys off this.
      farmer_id: form.farmer_id ?? null,
    };
    try {
      if (member) {
        await api.updateFarmMember(token, farmId, member.member_id, payload);
      } else {
        await api.createFarmMember(token, farmId, payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={member ? `Edit ${member.member_name}` : 'Add member'} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name *">
            <input required value={form.member_name} onChange={(e) => set('member_name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Relationship *">
            <select
              value={form.relationship_to_farm}
              onChange={(e) => set('relationship_to_farm', e.target.value as RelationshipToFarm)}
              className={inputCls}
            >
              {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Email">
            <input type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Phone">
            <input value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Title / role">
            <input value={form.title_role ?? ''} onChange={(e) => set('title_role', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Portal account">
            {form.farmer_id != null ? (
              <div className="flex items-center gap-2 text-sm border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                <span className="font-mono text-xs text-gray-500">#{form.farmer_id}</span>
                <span className="text-gray-800 truncate">
                  {linkedLabel}
                </span>
                <button
                  type="button"
                  onClick={() => { set('farmer_id', null); setSelectedProducer(null); setProducerQuery(''); }}
                  className="ml-auto text-gray-400 hover:text-gray-700"
                  aria-label="Unlink portal account"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  value={producerQuery}
                  onChange={(e) => setProducerQuery(e.target.value)}
                  placeholder="Search producers by name or email…"
                  title="Links this member to a producer portal account — enables the producer's farm view. Leave empty for members without accounts."
                  className={inputCls}
                />
                {producerMatches.length > 0 && (
                  <ul className="mt-1 border border-gray-200 rounded-md divide-y divide-gray-100 max-h-44 overflow-y-auto bg-white shadow-sm">
                    {producerMatches.map((p) => (
                      <li key={p.farmer_id}>
                        <button
                          type="button"
                          onClick={() => { set('farmer_id', p.farmer_id); setSelectedProducer(p); setProducerQuery(''); }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-green-50"
                        >
                          <span className="font-medium text-gray-900">{p.full_name}</span>
                          <span className="text-gray-500"> ({p.email})</span>
                          <span className="ml-1.5 font-mono text-xs text-gray-400">#{p.farmer_id}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {producerQuery.trim().length >= 2 && producerSearching && (
                  <p className="mt-1 text-xs text-gray-400">Searching…</p>
                )}
                {producerQuery.trim().length >= 2 && !producerSearching && producerMatches.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No producers match.</p>
                )}
              </div>
            )}
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-5 pt-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="Only one primary contact per farm — setting this demotes the current one.">
            <input type="checkbox" checked={form.is_primary_contact ?? false}
              onChange={(e) => set('is_primary_contact', e.target.checked)} />
            Primary contact
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="Receives farm-scoped email.">
            <input type="checkbox" checked={form.on_email_chain ?? true}
              onChange={(e) => set('on_email_chain', e.target.checked)} />
            On email chain
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="May sign for the farm (enforced on signature workflows in a later Build).">
            <input type="checkbox" checked={form.signature_authority ?? false}
              onChange={(e) => set('signature_authority', e.target.checked)} />
            Signature authority
          </label>
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {member ? 'Save changes' : 'Add member'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Shared bits (house styles per AdminFarmersPanel)
// ============================================================================
const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Kpi({ label, value, tone, icon }: { label: string; value: number; tone: 'ok' | 'warn' | 'amp'; icon: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'border-emerald-100 bg-emerald-50/50'
    : tone === 'amp' ? 'border-uif-green/20 bg-uif-green/5'
    : 'border-amber-100 bg-amber-50/50';
  return (
    <div className={'rounded-lg border p-3 ' + cls}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'amp'; children: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : tone === 'amp' ? 'bg-uif-green/5 border-uif-green/30 text-uif-green-dark'
    : 'bg-amber-50 border-amber-200 text-amber-800';
  return <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{children}</span>;
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
